// =============================================================================
// E7 FRONT DOOR — pure logic for the 7-card guided first-login flow, ported
// into throughline (LANE E7-PORT-THROUGHLINE) from apps/web/src/lib/
// onboarding.ts. docs/diagrams/c4-e7-front-door-2026-07-17.html + docs/
// design/first-login-profile-setup-2026-07-09.md is the spec; this module
// carries the SAME decisions (which step is next, what a suggestion looks
// like, the preview reader) — the meaning ports verbatim, only the file
// layout changes to fit this app's conventions.
//
// No React, no DOM, no network — every function here is a plain data
// transform so it is unit-testable directly, matching this app's OWN house
// idiom (onboardingStep.ts/homeRouteGate.ts/jobBuckets.ts/dragRehome.ts: "the
// decision is a pure module," since this repo has no jsdom/React-Testing-
// Library setup — see onboardingStep.ts's own header). UI wiring lives in
// components/FrontDoorCards.tsx + components/frontDoorCardBodies.tsx; the
// gate decision (whether to show these cards at all) lives in
// lib/useFrontDoorGate.ts, driven by shouldShowFrontDoorCards below.
//
// Named "frontDoorCards" (not "onboarding") deliberately — this app already
// has an unrelated concept called onboarding (onboardingStep.ts's 5-node
// corpus-discovery arc, OnboardingStep/resolveOnboardingStep). The E7 cards
// are a NEW, separate, composed-in-front phase (profile facts, gated on
// user_profiles.onboarded_at/thinness) — reusing the word "onboarding" for
// both would make every import ambiguous. See App.tsx's own comment on how
// the two phases compose into one continuous first hour.
// =============================================================================

import { normalizeLegacyLocation } from '@careeropt/schema';
import type { OnboardingCardPatch, RelocationPosture } from './api';

// Re-exported so callers (frontDoorCardBodies.tsx, FrontDoorCards.tsx) can
// import both the wire type and this module's own RELOCATION_POSTURES
// display list from one place, without a second import line back to api.ts
// just for the type.
export type { RelocationPosture } from './api';

/** The 7 cards, in flow order. One question per card (rule 2). */
export const FRONT_DOOR_STEP_IDS = [
  'preferred_name',
  'home_country',
  'postal_radius',
  'work_rights',
  'relocation',
  'contact',
  'finale',
] as const;

export type FrontDoorStepId = (typeof FRONT_DOOR_STEP_IDS)[number];

export const TOTAL_FRONT_DOOR_STEPS = FRONT_DOOR_STEP_IDS.length;

/** Clamp a step index into the valid [0, TOTAL_FRONT_DOOR_STEPS - 1] range —
 *  used by both "next" (don't walk off the finale) and "back" (don't walk
 *  before card 1). */
export function clampFrontDoorStepIndex(index: number): number {
  return Math.min(Math.max(index, 0), TOTAL_FRONT_DOOR_STEPS - 1);
}

/** Card 3's fixed v1 chip set (miles). The API validates a broader honest
 *  range (1-500) for future "radius tuning per search scenario" — the UI
 *  offers exactly these three, per the design doc. */
export const RADIUS_CHIPS_MILES = [25, 50, 100] as const;
export type RadiusMiles = (typeof RADIUS_CHIPS_MILES)[number];

/** Card 5's v1 three-way posture (design doc v1, "stay-local / domestic /
 *  international"). Stored in attributes.relocationPosture — structurally the
 *  SAME union as api.ts's own RelocationPosture (mirrors apps/web's own
 *  onboarding.ts, which independently derives this from its own literal
 *  array rather than importing api.ts's type; kept here for the same reason:
 *  RELOCATION_POSTURES is the display list this module owns, api.ts's type is
 *  the wire contract — structurally identical, deliberately not the same
 *  declaration). */
export const RELOCATION_POSTURES: ReadonlyArray<{ value: RelocationPosture; label: string }> = [
  { value: 'stay_local', label: 'Stay local' },
  { value: 'domestic', label: 'Open within my country' },
  { value: 'international', label: 'Open internationally' },
] as const;

/**
 * Card 1's SUGGESTION — never a decision, never auto-saved (NO INVENTION:
 * suggestion != decision). The auth session's OAuth display name (already
 * server-truth, served via GET /auth/me — useAuth.ts's SignedInUser.
 * displayName) is a full name ("Jason O'Rourke"); this splits off the first
 * token as a proposed "what should Meira call you" default. Purely a
 * presentational transform of an ALREADY-SERVED string — it invents nothing
 * about the user, it just proposes which substring of a real, already-true
 * name to prefill. The card still requires the user's own edit/confirm
 * before anything saves.
 */
export function deriveSuggestedPreferredName(displayName: string | null | undefined): string | null {
  if (!displayName) return null;
  const trimmed = displayName.trim();
  if (!trimmed) return null;
  const first = trimmed.split(/\s+/)[0];
  return first || null;
}

/**
 * Card 5's best-effort structured half of "target_markets/target_geo_ids
 * (EXISTS — posture + targets)" (the C4's own words for this card's write
 * shape). Reuses packages/schema/src/geo.ts's ALREADY-BUILT
 * normalizeLegacyLocation — its own header says exactly this: "REUSES the
 * exact token cues... Returns null when nothing matches, so the caller
 * KEEPS the legacy string (no data loss)". targetMarkets (the free-text
 * declarations, verbatim) is the source of truth this always writes
 * alongside; targetGeoIds is this best-effort structured VIEW over the same
 * declarations for whichever ones happen to map onto GEO_SEED's closed
 * token set — never the other way around, and never a reason to reject or
 * alter an un-mappable entry.
 */
export function deriveTargetGeoIds(targetMarkets: string[]): string[] {
  const ids: string[] = [];
  for (const market of targetMarkets) {
    const id = normalizeLegacyLocation(market);
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** Add/remove a value from a list (the work-rights multi-select's toggle
 *  shape). Generic + no case transform of its own — callers normalize case
 *  (e.g. uppercasing an ISO country code) before calling. */
export function toggleInArray<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * E7-WALK-WAVE W1 (his live 3-screenshot test, 2026-07-17): card 5's stored
 * shape evolved from a single posture to a multi-select array — a person can
 * be local AND open domestically AND interested internationally, all at
 * once. attributes.relocationPostures (the array) is the field this app
 * writes going forward; attributes.relocationPosture (the original singular
 * key) is NEVER dropped from the schema/type — additive evolution only — so
 * a row saved before this fix still round-trips correctly here: it reads as
 * a one-element array. The plural key, when present (even as `[]`, though
 * this card's own Continue gate never lets a save leave with zero
 * selections — see RelocationCard), always wins over the singular one; the
 * singular key only matters for rows that predate W1 and have never been
 * re-saved since.
 */
export function resolveRelocationPostures(attributes: {
  relocationPostures?: unknown;
  relocationPosture?: RelocationPosture | null;
}): RelocationPosture[] {
  if (Array.isArray(attributes.relocationPostures)) {
    const known = new Set<string>(RELOCATION_POSTURES.map((p) => p.value));
    return attributes.relocationPostures.filter(
      (v): v is RelocationPosture => typeof v === 'string' && known.has(v),
    );
  }
  return attributes.relocationPosture ? [attributes.relocationPosture] : [];
}

/**
 * E7-WALK-WAVE W2 (his live test, screenshot-annotated "Needs a no other
 * option upfront", 2026-07-17): card 4's read-side precedence. Re-entering
 * the card must show the "No others" chip selected when the user already
 * declared it — but it must ALSO never show that declaration sitting next
 * to a stale set of "selected" countries. attributes.workRightsNone, when
 * true, therefore wins over profile.workRights for DISPLAY, even if a real
 * declaration from BEFORE this "none" answer still lingers in the resolved
 * profile (the server-side CRITICAL GUARD in profile.routes.ts deliberately
 * never clears that array itself, to avoid ever writing workRights:[] and
 * poisoning the citizenship-derived default — see that file's own doc
 * comment). This function is the one place that reconciles the two fields
 * into a single honest view for the card to render.
 */
export function resolveWorkRightsSelection(profile: {
  workRights: string[];
  attributes: { workRightsNone?: unknown };
}): { selected: string[]; noneDeclared: boolean } {
  const noneDeclared = profile.attributes.workRightsNone === true;
  return { selected: noneDeclared ? [] : profile.workRights, noneDeclared };
}

/**
 * E7-WALK-WAVE W2 — the write-side half of the CRITICAL GUARD. WorkRights-
 * Card's own onSave hands back its local {codes, none} state; this derives
 * the EXACT patch to send. A "none" answer sends ONLY workRightsNone: true
 * — never `workRights` in any form, not even `[]` (an empty-array override
 * would permanently disable resolveUserProfile's citizenship-derived
 * default, since `overrides` only ever grows — see profile.routes.ts's own
 * doc comment on the workRightsNone schema field). A real declaration sends
 * `workRights` plus `workRightsNone: null`, clearing any earlier "none"
 * answer — the save-side half of the mutual-exclusion rule (WorkRightsCard's
 * own toggle handlers are the UI-side half). Pure + exported so this exact
 * guard is unit-tested without a live PUT call.
 */
export function buildWorkRightsPatch(
  answer: { codes: string[]; none: boolean },
): Pick<OnboardingCardPatch, 'workRights' | 'workRightsNone'> {
  if (answer.none) return { workRightsNone: true };
  return { workRights: answer.codes, workRightsNone: null };
}

/**
 * E7-WALK-WAVE W2 — WorkRightsCard's own mutual-exclusion rule, the UI-side
 * half (buildWorkRightsPatch above is the save-side half): toggling a real
 * country always clears a prior "declared none" — the two states can never
 * coexist client-side, mirroring resolveWorkRightsSelection's read-side
 * precedence. Pure so this one real decision behind the card's onClick
 * handler is unit-tested without a DOM (this repo has no jsdom/RTL setup —
 * see this module's own header).
 */
export function toggleWorkRightsCountry(
  state: { picked: string[]; none: boolean },
  code: string,
): { picked: string[]; none: boolean } {
  return { picked: toggleInArray(state.picked, code), none: false };
}

/** E7-WALK-WAVE W2 — the mirror of toggleWorkRightsCountry above: declaring
 *  "No others" always clears every country selection. */
export function selectWorkRightsNone(): { picked: string[]; none: boolean } {
  return { picked: [], none: true };
}

/**
 * The preview-mode reader: `?onboarding=preview` on the URL. A signed-in
 * user can open the flow WITHOUT the never-trap gate offering it, to test or
 * re-walk it. Preview does NOT change how a card saves (every PUT /api/
 * profile call the flow makes behaves identically in and out of preview —
 * answers DO save to the real profile) — the ONLY thing preview withholds is
 * the POST /api/profile/onboarding/complete stamp at the end, so re-opening
 * the flow later doesn't require un-completing anything. Same reader as
 * apps/web's lib/onboarding.ts (identical URL contract, both frontends).
 */
export function isOnboardingPreview(search: string): boolean {
  return new URLSearchParams(search).get('onboarding') === 'preview';
}

/**
 * THE GATE THIS MODULE ADDS (lib/useFrontDoorGate.ts's one real decision,
 * extracted here so it's unit-testable without a DB/fetch — same "the
 * decision is a pure module" discipline onboardingStep.ts's
 * resolveOnboardingStep and homeRouteGate.ts's shouldRenderHome already
 * establish). Precedence, highest first:
 *   1. finished (this session already completed or skip-alled the 7 cards) ->
 *      never show them again this mount, regardless of what the server says
 *      (mirrors onboardingStep.ts's own "explicit skip always wins").
 *   2. preview -> always show (his test path bypasses eligibility entirely,
 *      exactly like apps/web's isOnboardingPreview/FrontDoor.tsx contract) —
 *      but preview never sets `finished` via the real stamp, only via this
 *      session's own local "I clicked done" flag.
 *   3. eligibleForOnboarding === true (server truth, GET /profile/onboarding
 *      — the onboarded_at-null AND profile-thin rule, computed server-side
 *      by computeOnboardingEligibility) -> show.
 *   4. Anything else (false, or null/unknown while the read is in flight or
 *      failed) -> don't show. Fails CLOSED toward NOT showing the cards —
 *      mirrors apps/web's own OnboardingGate doc comment ("a network hiccup
 *      must never trap an existing user behind the flow") and this app's own
 *      useOnboardingGate "unknown, not empty" discipline: an existing user
 *      must never see profile-fact cards flash in front of them because a
 *      read hadn't resolved yet.
 */
export function shouldShowFrontDoorCards(args: {
  eligibleForOnboarding: boolean | null;
  preview: boolean;
  finished: boolean;
}): boolean {
  if (args.finished) return false;
  if (args.preview) return true;
  return args.eligibleForOnboarding === true;
}

/** One card's visual/save state — drives the shell's chrome (spinner, error
 *  banner, skipped dimming) identically in the live flow and the StyleGuide
 *  matrix. 'suggested' applies only to cards with a live suggestion
 *  (preferred name, home country) — a card with no suggestion goes straight
 *  from 'idle' to 'confirmed'. */
export type CardVisualState = 'idle' | 'suggested' | 'confirmed' | 'skipped' | 'error' | 'saving';

/** Compose a card's visual state from what's already been served/decided —
 *  the SAME small decision the live flow and a StyleGuide preview both need,
 *  kept in one place so they can never silently drift. Precedence: an
 *  in-flight save or a save error wins over everything (freshest user
 *  action); a confirmed value wins over a stray suggestion; a skip only
 *  shows once nothing else applies. */
export function composeCardVisualState(args: {
  saving?: boolean;
  error?: boolean;
  confirmedValue: unknown;
  suggestion?: unknown;
  skipped?: boolean;
}): CardVisualState {
  if (args.saving) return 'saving';
  if (args.error) return 'error';
  const hasConfirmed =
    args.confirmedValue != null &&
    !(Array.isArray(args.confirmedValue) && args.confirmedValue.length === 0) &&
    args.confirmedValue !== '';
  if (hasConfirmed) return 'confirmed';
  if (args.skipped) return 'skipped';
  if (args.suggestion != null && args.suggestion !== '') return 'suggested';
  return 'idle';
}
